import type { BridgeToApiPromptResultMessage } from '../../../types/bridge-to-api-messages.js';
import type { CliE2eeRuntime } from '../../../lib/e2ee/index.js';
import type { SendPromptOptions } from '../clients/acp-client.js';
import type { LogFn } from '../../../log.js';
import { augmentPromptResultAuthFields } from '../send-prompt-result-augment.js';
import { fetchSessionAttachmentPayloadsForAgent, type SessionAttachmentRef } from '../fetch-session-attachments.js';

export type ResolveSendPromptImagesResult =
  | { ok: true; sendOpts: SendPromptOptions }
  | { ok: false; errorResult: BridgeToApiPromptResultMessage };

export async function resolveSendPromptImages(params: {
  attachments?: SessionAttachmentRef[];
  sessionId?: string;
  promptId?: string;
  runId?: string;
  agentType?: string | null;
  followUpCatalogPromptId?: string | null;
  cloudApiBaseUrl?: string;
  getCloudAccessToken?: () => string;
  e2ee?: CliE2eeRuntime;
  log: LogFn;
}): Promise<ResolveSendPromptImagesResult> {
  const {
    attachments,
    sessionId,
    promptId,
    runId,
    agentType,
    followUpCatalogPromptId,
    cloudApiBaseUrl,
    getCloudAccessToken,
    e2ee,
    log,
  } = params;

  if (!attachments || attachments.length === 0) {
    return { ok: true, sendOpts: {} };
  }

  if (!sessionId || !cloudApiBaseUrl || !getCloudAccessToken) {
    return {
      ok: false,
      errorResult: {
        type: 'prompt_result',
        id: promptId,
        ...(sessionId ? { sessionId } : {}),
        ...(runId ? { runId } : {}),
        success: false,
        error: 'Prompt includes images but the bridge is not configured with a cloud API URL and token to fetch them.',
        ...(followUpCatalogPromptId != null && followUpCatalogPromptId !== ''
          ? { followUpCatalogPromptId }
          : {}),
        ...augmentPromptResultAuthFields(agentType, 'missing cloud for images download'),
      },
    };
  }

  const resolved = await fetchSessionAttachmentPayloadsForAgent({
    attachments,
    sessionId,
    cloudApiBaseUrl,
    getCloudAccessToken,
    e2ee,
    log,
  });

  if (!resolved.ok) {
    return {
      ok: false,
      errorResult: {
        type: 'prompt_result',
        id: promptId,
        ...(sessionId ? { sessionId } : {}),
        ...(runId ? { runId } : {}),
        success: false,
        error: resolved.error,
        ...(followUpCatalogPromptId != null && followUpCatalogPromptId !== ''
          ? { followUpCatalogPromptId }
          : {}),
        ...augmentPromptResultAuthFields(agentType, resolved.error),
      },
    };
  }

  return { ok: true, sendOpts: { images: resolved.images } };
}
